import { CIRCLE_TIERS, GATE_MIN_REVIEWERS, GATEKEEPER_TIERS, PLACE_TIERS, VET_ELIGIBLE_AFTER_DAYS, } from './constants.js';
import { tierOf } from './tier-of.js';
const ALLOW = { allow: true, scope: 'full' };
const allowAs = (scope) => ({ allow: true, scope });
const deny = (reason) => ({ allow: false, reason });
const DAY_MS = 86_400_000;
function daysSince(d, now) {
    return d ? (now.getTime() - d.getTime()) / DAY_MS : 0;
}
/** §5.1.1a: who sees whom, and how much (decision 38). */
export function seeTierScope(viewer, target) {
    if (viewer !== 'anonymous')
        return 'full';
    return target === 'anonymous' ? 'card' : 'silhouette';
}
/**
 * What a person's ribbon says (decision 172): Eligible is Anonymous with a vouch, clips not sent yet
 * (decision 149), where the viewer was told so (`eligible`, see_eligible); otherwise their tier.
 */
export function tierWord(tier, eligible = false) {
    if (tier === 'anonymous')
        return eligible ? 'Eligible' : 'Anonymous';
    return tier === 'pending' ? 'Pending' : 'Trusted';
}
/** Kept for callers that only need yes/no. Everyone sees everyone at some scope. */
export function canSeeTier(_viewer, _target) {
    return true;
}
/** §5.1.1a: who may open a conversation with whom. */
export function canInitiateTier(viewer, target) {
    if (viewer === 'trusted')
        return true;
    if (viewer === 'pending')
        return target === 'anonymous';
    return false;
}
/**
 * What a refused action says, for the reasons a member can do something about or should be told
 * about plainly (TS-18 hold, TS-12 restriction). Anything else keeps the terse form.
 */
export function refusalText(reason) {
    if (reason === 'held')
        return 'Your account is on hold while a moderator reviews a report. Messages, taps, statuses, spots and invites wait until then.';
    if (reason === 'restricted')
        return 'Your account is restricted for now: no invites, no vetting, no new conversations.';
    if (reason === 'meet_paused')
        return 'Meet is paused for you after missed meets. You can still chat.';
    if (reason === 'their_meet_paused')
        return 'Meet is paused for them right now.';
    return `Not available: ${reason}.`;
}
export function can(viewer, action, target, ctx = {}) {
    const now = ctx.now ?? new Date();
    const vt = tierOf(viewer.state);
    if (!vt)
        return deny('removed');
    // Things that never involve a target.
    switch (action) {
        case 'see_layer_spots':
            return ALLOW;
        case 'see_layer_anonymous':
            return ctx.viewerTravelling ? deny('travelling') : ALLOW;
        // Decision 172: Eligible is Anonymous with a vouch, which only Trusted members are told (the Gate's
        // Eligible tab, decision 164). Anyone else sees them as Anonymous, on the Anonymous layer; a
        // travelling member has neither layer (FR-KNK-05).
        case 'see_eligible':
            return vt === 'trusted' ? ALLOW : deny('tier');
        case 'see_layer_eligible':
            if (vt !== 'trusted')
                return deny('tier');
            return ctx.viewerTravelling ? deny('travelling') : ALLOW;
        case 'see_layer_cruisers':
        case 'see_layer_gate':
            // Decision 38: Anonymous users get these layers as silhouettes, except in a travel city (FR-KNK-05).
            if (vt !== 'anonymous')
                return ALLOW;
            return ctx.viewerTravelling ? deny('travelling') : allowAs('silhouette');
        // Events are Trusted members' (§5.1.1, WF-GRP-02): Pending and Anonymous users don't see them at all.
        case 'see_layer_events':
        // Groups too (decision 92): Trusted members' only, as events are.
        case 'see_layer_groups':
            return vt === 'trusted' ? ALLOW : deny('tier');
        case 'see_gate_queue':
            return vt === 'trusted' ? ALLOW : deny('tier');
        // Starring a spot is a vetted member's, as starring a person is: the stars only ever show on
        // Favorites, which is Plus, and Plus waits for vetting (FR-SPT-11, issue 8).
        case 'favorite_spot':
            return vt === 'trusted' && viewer.state !== 'restricted' ? ALLOW : deny('tier');
        case 'request_vet':
            return viewer.state === 'pending' ? ALLOW : deny('tier');
        // A photo on the Wall is in front of the whole area (decision 178): vetted members only, restricted
        // ones included, so an unvetted account can't put images there. Not while held.
        case 'post_wall_image':
            if (viewer.held)
                return deny('held');
            return vt === 'trusted' ? ALLOW : deny('tier');
        // Vouch for me (decision 197) shows for someone Anonymous with no vouch, a photo (no pin shows without
        // one, FR-ANO-08) and no hold; it's on from sign-up until they turn it off. Only vetted members are
        // told (see_vouch_ask), who are the ones who vouch.
        case 'ask_vouch':
            if (vt !== 'anonymous' || ctx.viewerVouched)
                return deny('tier');
            if (viewer.held)
                return deny('held');
            return ctx.viewerHasPhoto ? ALLOW : deny('no_photo');
        case 'see_vouch_ask':
            return vt === 'trusted' ? ALLOW : deny('tier');
        case 'set_status':
        case 'create_spot':
        // Hosting an event, or asking into one, is a new conversation: not while held or restricted (FR-EVT-03).
        case 'create_event':
        case 'join_event':
        case 'create_group':
        case 'join_group':
            if (viewer.held)
                return deny('held');
            if (viewer.state === 'restricted')
                return deny('restricted');
            return vt === 'trusted' ? ALLOW : deny('tier');
        // The app as a spot-finder for anyone (decision 79): check in, On my way, tips and flags, whatever
        // the tier. Who's at a spot stays Trusted-only (decision 59), and adding or editing a spot too.
        case 'check_in':
        case 'comment_spot':
        case 'flag_spot':
            if (viewer.held)
                return deny('held');
            if (viewer.state === 'restricted')
                return deny('restricted');
            return ALLOW;
        case 'invite':
            return canInvite(viewer, now);
        case 'report':
            return ALLOW;
        default:
            break;
    }
    // Everything below needs a target.
    if (!target)
        return deny('target_missing');
    if (target.id === viewer.id)
        return deny('self');
    const tt = tierOf(target.state);
    if (!tt)
        return deny('removed');
    if (ctx.blocked)
        return deny('blocked');
    switch (action) {
        case 'block':
            return ALLOW;
        case 'favorite':
            return vt === 'trusted' && viewer.state !== 'restricted' ? ALLOW : deny('tier');
        case 'see_pin': {
            const scope = seeTierScope(vt, tt);
            if (target.ghost)
                return deny('ghost');
            if (target.held || shadowedFrom(viewer, target))
                return deny('hidden');
            const v = target.visibility ?? {};
            if (v.showOnMap === false)
                return deny('hidden');
            if (v.messagedOnly && !ctx.conversationExists)
                return deny('messaged_only');
            if (scope === 'silhouette') {
                if (v.silhouetteToAnonymous === false)
                    return deny('hidden');
                if (ctx.targetTravelling || ctx.viewerTravelling)
                    return deny('travelling');
            }
            return allowAs(scope);
        }
        case 'knock': {
            if (vt !== 'anonymous')
                return deny('tier');
            if (tt === 'anonymous')
                return deny('tier');
            if (viewer.held)
                return deny('held');
            const seen = can(viewer, 'see_pin', target, ctx);
            if (!seen.allow)
                return seen;
            if (target.visibility?.allowKnocks === false)
                return deny('knocks_off');
            if (!ctx.viewerHasPhoto)
                return deny('no_photo');
            if (ctx.knockedRecently)
                return deny('already_knocked');
            return ALLOW;
        }
        case 'open_profile': {
            // Decision 20: an Anonymous user may read the profile of a member who opened a conversation with them.
            if (vt === 'anonymous' && tt !== 'anonymous') {
                return ctx.conversationExists && ctx.conversationOpenedBy === target.id ? ALLOW : deny('tier');
            }
            // Decision 21: Anonymous users see each other as a card only.
            if (vt === 'anonymous' && tt === 'anonymous')
                return deny('tier');
            if (target.held || shadowedFrom(viewer, target))
                return deny('hidden');
            if (target.ghost && !ctx.conversationExists && !ctx.viewerKnowsTarget)
                return deny('ghost');
            if (vt === 'anonymous')
                return deny('tier'); // defensive: handled above, never a full profile from a silhouette
            const v = target.visibility ?? {};
            if (v.messagedOnly && !ctx.conversationExists)
                return deny('messaged_only');
            return ALLOW;
        }
        case 'open_conversation': {
            if (viewer.held)
                return deny('held');
            if (viewer.state === 'restricted')
                return deny('restricted');
            if (!canInitiateTier(vt, tt))
                return deny('tier');
            if (target.held || shadowedFrom(viewer, target))
                return deny('hidden');
            if (target.ghost && !ctx.viewerKnowsTarget)
                return deny('ghost');
            const v = target.visibility ?? {};
            if (v.gatekeepersOnly && !isGatekeeper(viewer))
                return deny('gatekeepers_only');
            return ALLOW;
        }
        case 'send_message': {
            // FR-ANO-04: once a conversation exists, both sides can use it, whatever their tier.
            if (viewer.held)
                return deny('held');
            if (target.held || shadowedFrom(viewer, target))
                return deny('hidden');
            return ctx.conversationExists ? ALLOW : deny('no_conversation');
        }
        // Meet (§5.17, FR-MET-01): two Trusted members who already talk, neither held, paused or blocked.
        case 'meet': {
            if (viewer.held)
                return deny('held');
            if (viewer.state === 'restricted')
                return deny('restricted');
            if (vt !== 'trusted' || tt !== 'trusted')
                return deny('tier');
            if (target.held || shadowedFrom(viewer, target))
                return deny('hidden');
            if (!ctx.conversationExists)
                return deny('no_conversation');
            if (ctx.viewerMeetPaused)
                return deny('meet_paused');
            if (ctx.targetMeetPaused)
                return deny('their_meet_paused');
            return ALLOW;
        }
        case 'vouch_in_chat': {
            const inv = canInvite(viewer, now);
            if (!inv.allow)
                return inv;
            if (tt !== 'anonymous' || ctx.targetVouched)
                return deny('tier');
            return ctx.conversationExists || viewer.isFounder ? ALLOW : deny('no_conversation');
        }
        case 'vet': {
            if (viewer.held)
                return deny('held');
            if (viewer.state !== 'trusted')
                return deny(vt === 'trusted' ? 'restricted' : 'tier');
            if (target.state !== 'pending')
                return deny('tier');
            // Founders review anyone, their own invitees included (decision 48).
            if (viewer.isFounder)
                return ALLOW;
            if (ctx.targetInviterId === viewer.id)
                return deny('own_invitee');
            // Moderators are picked by the owner, so they skip the wait (bugs.md). It's per region (bugs.md).
            if (!viewer.isModerator &&
                daysSince(viewer.trustedSince, now) < (viewer.vetWaitDays ?? VET_ELIGIBLE_AFTER_DAYS))
                return deny('not_eligible_yet');
            if (viewer.vettingRights === false)
                return deny('no_vetting_rights');
            if ((viewer.openCases ?? 0) > 0)
                return deny('open_case');
            if (target.vetPreference === 'gatekeepers' && !isGatekeeper(viewer))
                return deny('not_gatekeeper');
            return ALLOW;
        }
        default:
            return deny('tier');
    }
}
/**
 * How far a waiting member's clips reach (bugs.md: nobody's pin is orphaned): their region's Gate
 * radius, or, with fewer than GATE_MIN_REVIEWERS eligible reviewers inside it, out to the nearest
 * that many, however far. `eligibleKm` is each reviewer's distance who may vet them. With nobody
 * eligible anywhere it's the radius.
 */
export function gateReachKm(radiusKm, eligibleKm) {
    const sorted = [...eligibleKm].sort((a, b) => a - b);
    const nth = sorted[Math.min(GATE_MIN_REVIEWERS, sorted.length) - 1];
    return nth === undefined ? radiusKm : Math.max(radiusKm, nth);
}
/** Someone shadow-held is hidden from everyone but themselves (decision 129). */
function shadowedFrom(viewer, target) {
    return !!target.shadowed && target.id !== viewer.id;
}
/**
 * Vouch and vet at once: founders, moderators and the owner, Trusted and not held, may skip the Gate for
 * someone they vouch for. They're on the chain as both voucher and vetter.
 */
export function maySkipVetting(viewer) {
    return (!!viewer.isFounder || !!viewer.isModerator) && viewer.state === 'trusted' && !viewer.held;
}
/**
 * Vet now on the console's Gate (decision 139): the owner, on anyone waiting there; a moderator, Trusted
 * and not held, only on someone they vouched for themselves (their voucher on the chain), as Skip vetting
 * would have let them when they vouched. Whoever does it is their vetter on the chain. Who's at the Gate
 * and not held is the route's to check; this is who may.
 */
export function mayVetNow(viewer, target) {
    if (viewer.role === 'owner')
        return true;
    if (viewer.role !== 'moderator' || viewer.state !== 'trusted' || viewer.held)
        return false;
    return !!target.inviterId && target.inviterId === viewer.id;
}
/** Gatekeeper tier from the vet count: 0 = none, 1 at 5 vets, 2 at 25, 3 at 100 (FR-VET-07). */
export function gatekeeperTier(vetCount = 0) {
    let tier = 0;
    GATEKEEPER_TIERS.forEach((n, i) => {
        if (vetCount >= n)
            tier = (i + 1);
    });
    return tier;
}
/** The circle's badge tier: Connected, Well Connected, Legend (decision 65) from the size of a member's circle; a badge, nothing it unlocks. */
export function circleTier(circle = 0) {
    let tier = 0;
    CIRCLE_TIERS.forEach((n, i) => {
        if (circle >= n)
            tier = (i + 1);
    });
    return tier;
}
/** Scout, Tracker, Trailblazer (decision 67) from places added; a badge, nothing it unlocks. */
export function placeTier(places = 0) {
    let tier = 0;
    PLACE_TIERS.forEach((n, i) => {
        if (places >= n)
            tier = (i + 1);
    });
    return tier;
}
function isGatekeeper(a) {
    return gatekeeperTier(a.vetCount) >= 1;
}
function canInvite(viewer, now) {
    if (viewer.held)
        return deny('held');
    if (viewer.state === 'restricted')
        return deny('restricted');
    if (viewer.state !== 'trusted')
        return deny('tier');
    // Founders have no tenure or quota limits (decision 48); a region can lift them for everyone (decision 49).
    if (viewer.isFounder || viewer.invitesUnlimited)
        return ALLOW;
    if (daysSince(viewer.trustedSince, now) < VET_ELIGIBLE_AFTER_DAYS)
        return deny('not_eligible_yet');
    if ((viewer.invitesLeft ?? 0) <= 0)
        return deny('no_invites');
    return ALLOW;
}
/** Convenience for the client: all the actions a viewer may take on a target, as a set. */
export function allowedActions(viewer, target, ctx = {}) {
    const actions = [
        'see_pin',
        'open_profile',
        'open_conversation',
        'send_message',
        'vouch_in_chat',
        'vet',
        'favorite',
        'block',
        'report',
    ];
    return new Set(actions.filter((a) => can(viewer, a, target, ctx).allow));
}
//# sourceMappingURL=policy.js.map